<template>
  <Teleport to="body">
    <Transition :name="transitionName" appear>
      <div 
        v-if="isModalVisible" 
        class="app-modal"
        :class="[
          `app-modal--${size}`,
          { 'app-modal--gradient-header': gradientHeader },
          { 'app-modal--modern': modern }
        ]"
        role="dialog"
        :aria-labelledby="title ? 'modal-title' : undefined"
        :aria-describedby="message ? 'modal-message' : undefined"
        aria-modal="true"
        @click="handleBackdropClick"
        @keydown.esc="handleEscapeKey"
        @keydown.tab="handleTabKey"
        ref="modalRef"
      >
        <div class="app-modal__container" @click.stop ref="containerRef">
          <!-- Custom Header Slot -->
          <div v-if="$slots.header" class="app-modal__header app-modal__header--custom">
            <slot name="header" :close="onCancel"></slot>
          </div>
          
          <!-- Default Header -->
          <div v-else-if="title || subtitle || icon || showClose" class="app-modal__header" :class="{ 'app-modal__header--gradient': gradientHeader }">
            <div class="app-modal__header-content">
              <div v-if="icon" class="app-modal__icon">
                <svg v-if="typeof icon === 'string'" viewBox="0 0 24 24" width="24" height="24" v-html="icon"></svg>
                <component v-else :is="icon" />
              </div>
              <div class="app-modal__text">
                <h3 v-if="title" id="modal-title" class="app-modal__title">
                  {{ title }}
                </h3>
                <p v-if="subtitle" class="app-modal__subtitle">
                  {{ subtitle }}
                </p>
              </div>
            </div>            <button 
              v-if="showClose"
              class="app-modal__close" 
              @click="onCancel"
              :title="$t('common.close')"
              ref="closeButtonRef"
            >
              ✕
            </button>
          </div>

          <!-- Body -->
          <div class="app-modal__body" :class="{ 'app-modal__body--no-padding': noPadding }">
            <slot>
              <p v-if="message" id="modal-message" class="app-modal__message">
                {{ message }}
              </p>
            </slot>
          </div>

          <!-- Custom Footer Slot -->
          <div v-if="$slots.footer" class="app-modal__footer app-modal__footer--custom">
            <slot name="footer" :close="onCancel" :confirm="onConfirm"></slot>
          </div>
          
          <!-- Default Footer -->
          <div v-else-if="!$slots.default && (confirmText || cancelText)" class="app-modal__footer">
            <div class="app-modal__actions">
              <AppButton 
                v-if="cancelText"
                type="button"
                variant="outline" 
                @click="onCancel"
                class="app-modal__btn app-modal__btn--cancel"
                :disabled="loading"
              >
                {{ cancelText }}
              </AppButton>
              <AppButton 
                v-if="confirmText"
                type="button"
                :variant="confirmVariant" 
                @click="onConfirm"
                class="app-modal__btn app-modal__btn--confirm"
                :disabled="loading"
                :loading="loading"
              >
                {{ confirmText }}
              </AppButton>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
/// <reference types="vue" />
// @ts-ignore
import { ref, nextTick, onUnmounted, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import AppButton from './AppButton.vue'

// Define props interface
interface Props {
  title?: string
  subtitle?: string
  message?: string
  icon?: string | object | null
  confirmText?: string
  cancelText?: string
  confirmVariant?: 'primary' | 'danger' | 'warning' | 'success' | 'outline'
  closeOnBackdropClick?: boolean
  isVisible?: boolean
  show?: boolean
  showClose?: boolean
  size?: 'small' | 'medium' | 'large' | 'xlarge'
  gradientHeader?: boolean
  modern?: boolean
  noPadding?: boolean
  loading?: boolean
  transitionName?: string
  autoFocus?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  subtitle: '',
  message: '',
  icon: null,
  confirmText: '',
  cancelText: '',
  confirmVariant: 'primary',
  closeOnBackdropClick: true,
  isVisible: false,
  show: false,
  showClose: true,
  size: 'medium',
  gradientHeader: false,
  modern: false,
  noPadding: false,
  loading: false,
  transitionName: 'modal',
  autoFocus: true
})

// Define emits interface
interface Emits {
  confirm: []
  cancel: []
  close: []
  'update:show': [value: boolean]
}

const emit = defineEmits<Emits>()

// i18n
const { t } = useI18n()

// Refs
const modalRef = ref<HTMLElement | null>(null)
const containerRef = ref<HTMLElement | null>(null)
const closeButtonRef = ref<HTMLElement | null>(null)

// Computed visibility - supports both isVisible and show props
const isModalVisible = computed(() => props.isVisible || props.show)

// Methods
const onConfirm = (): void => {
  emit('confirm')
  emit('close')
  emit('update:show', false)
}

const onCancel = (): void => {
  emit('cancel')
  emit('close')
  emit('update:show', false)
}

const handleBackdropClick = (): void => {
  if (props.closeOnBackdropClick && !props.loading) {
    onCancel()
  }
}

const handleEscapeKey = (event: KeyboardEvent): void => {
  if (event.key === 'Escape' && !props.loading) {
    onCancel()
  }
}

// Focus trap functionality
const handleTabKey = (event: KeyboardEvent): void => {
  if (!isModalVisible.value || !containerRef.value) return

  const focusableElements = containerRef.value.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  )

  const firstElement = focusableElements[0] as HTMLElement
  const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement

  if (event.shiftKey && document.activeElement === firstElement) {
    lastElement?.focus()
    event.preventDefault()
  } else if (!event.shiftKey && document.activeElement === lastElement) {
    firstElement?.focus()
    event.preventDefault()
  }
}

// Auto-focus management
watch(() => isModalVisible.value, (newVal: boolean) => {
  if (newVal && props.autoFocus) {
    nextTick(() => {
      // Try to focus the close button first, then any focusable element
      if (closeButtonRef.value) {
        (closeButtonRef.value as HTMLElement).focus()
      } else if (containerRef.value) {
        const firstFocusable = containerRef.value.querySelector(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        ) as HTMLElement
        firstFocusable?.focus()
      }
    })
  }
})

// Prevent body scroll when modal is open
watch(() => isModalVisible.value, (newVal: boolean) => {
  if (newVal) {
    document.body.classList.add('modal-open')
    // Store current scroll position
    const scrollY = window.scrollY
    document.body.style.position = 'fixed'
    document.body.style.top = `-${scrollY}px`
    document.body.style.width = '100%'
  } else {
    document.body.classList.remove('modal-open')
    // Restore scroll position
    const scrollY = parseInt(document.body.style.top || '0') * -1
    document.body.style.position = ''
    document.body.style.top = ''
    document.body.style.width = ''
    window.scrollTo(0, scrollY)
  }
})

onUnmounted(() => {
  document.body.classList.remove('modal-open')
  // Restore scroll position if component is unmounted while modal is open
  const scrollY = parseInt(document.body.style.top || '0') * -1
  document.body.style.position = ''
  document.body.style.top = ''
  document.body.style.width = ''
  if (scrollY !== 0) {
    window.scrollTo(0, scrollY)
  }
})
</script>

<style scoped>
/* ==========================================================================
   MODAL OVERLAY
   ========================================================================== */

.app-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--z-modal, 10000); /* Use CSS variable with fallback */
  padding: 1rem;
  padding-top: calc(var(--top-bar-height, 72px) + var(--quick-actions-height, 60px) + 1rem);
  padding-bottom: 1rem;
  overflow-x: hidden;
  overflow-y: auto;
}

/* Modern variant with enhanced backdrop */
.app-modal--modern {
  background-color: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(8px);
}

/* ==========================================================================
   MODAL CONTAINER
   ========================================================================== */

.app-modal__container {
  position: relative;
  background: var(--surface-primary, white);
  border-radius: var(--radius-lg, 12px);
  box-shadow: var(--shadow-2xl, 0 25px 50px -12px rgba(0, 0, 0, 0.25));
  width: 100%;
  max-width: 500px;
  max-height: calc(100vh - var(--top-bar-height, 72px) - var(--quick-actions-height, 60px) - 2rem);
  margin: auto;
  display: flex;
  flex-direction: column;
  border: 1px solid var(--border-light, rgba(255, 255, 255, 0.1));
  transform: translateZ(0);
  overflow: hidden;
}

/* Modal sizes */
.app-modal--small .app-modal__container {
  max-width: 400px;
}

.app-modal--medium .app-modal__container {
  max-width: 500px;
}

.app-modal--large .app-modal__container {
  max-width: 700px;
}

.app-modal--xlarge .app-modal__container {
  max-width: 900px;
}

/* ==========================================================================
   MODAL HEADER
   ========================================================================== */

.app-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-md, 1rem);
  background: var(--surface-secondary, #f8f9fa);
  border-bottom: 1px solid var(--border-light, #e9ecef);
  flex-shrink: 0;
}

.app-modal__header--gradient {
  background: linear-gradient(135deg, var(--primary, #007bff) 0%, var(--primary-600, #0056b3) 100%);
  color: white;
  border-bottom: none;
}

.app-modal__header--custom {
  padding: 0;
  background: transparent;
  border-bottom: none;
}

.app-modal__header-content {
  display: flex;
  align-items: center;
  gap: var(--space-md, 1rem);
  flex: 1;
  min-width: 0;
  margin-right: var(--space-md, 1rem);
}

.app-modal__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  border-radius: var(--radius-lg, 12px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  flex-shrink: 0;
}

.app-modal__text {
  flex: 1;
  min-width: 0;
}

.app-modal__title {
  font-size: var(--font-size-xl, 1.25rem);
  font-weight: var(--font-weight-semibold, 600);
  color: var(--text-primary, #212529);
  margin: 0 0 var(--space-xs, 0.25rem) 0;
  line-height: 1.25;
}

.app-modal__header--gradient .app-modal__title {
  color: white;
}

.app-modal__subtitle {
  font-size: var(--font-size-sm, 0.875rem);
  color: var(--text-secondary, #6c757d);
  margin: 0;
  opacity: 0.9;
}

.app-modal__header--gradient .app-modal__subtitle {
  color: rgba(255, 255, 255, 0.9);
}

.app-modal__close {
  background: #ffffff !important;
  border: 2px solid #cccccc !important;
  border-radius: 50% !important;
  width: 40px !important;
  height: 40px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  cursor: pointer !important;
  transition: all 0.2s ease !important;
  color: #000000 !important;
  font-size: 20px !important;
  font-weight: bold !important;
  flex-shrink: 0 !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2) !important;
  position: relative !important;
  z-index: 10 !important;
}

.app-modal__header--gradient .app-modal__close {
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(10px) !important;
  border: 2px solid rgba(255, 255, 255, 0.8) !important;
  color: #000000 !important;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.3) !important;
}

.app-modal__close:hover {
  background: #f0f0f0 !important;
  border-color: #666666 !important;
  color: #000000 !important;
  transform: scale(1.1) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3) !important;
}

.app-modal__header--gradient .app-modal__close:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.app-modal__close:focus-visible {
  outline: 2px solid var(--primary, #007bff);
  outline-offset: 2px;
}

/* ==========================================================================
   MODAL BODY
   ========================================================================== */

.app-modal__body {
  padding: var(--space-lg, 1.5rem);
  overflow-y: auto;
  flex: 1;
}

.app-modal__body--no-padding {
  padding: 0;
}

.app-modal__message {
  font-size: var(--font-size-md, 1rem);
  color: var(--text-primary, #212529);
  margin: 0;
  line-height: 1.6;
}

/* ==========================================================================
   MODAL FOOTER
   ========================================================================== */

.app-modal__footer {
  background: var(--surface-secondary, #f8f9fa);
  padding: var(--space-lg, 1.5rem);
  border-top: 1px solid var(--border-light, #e9ecef);
  flex-shrink: 0;
}

.app-modal__footer--custom {
  padding: 0;
  background: transparent;
  border-top: none;
}

.app-modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-sm, 0.75rem);
}

.app-modal__btn {
  min-width: 100px;
}

/* ==========================================================================
   ANIMATIONS
   ========================================================================== */

.modal-enter-active, .modal-leave-active {
  transition: all 0.3s ease;
}

.modal-enter-from, .modal-leave-to {
  opacity: 0;
}

.modal-enter-active .app-modal__container,
.modal-leave-active .app-modal__container {
  transition: all 0.3s ease;
}

.modal-enter-from .app-modal__container,
.modal-leave-to .app-modal__container {
  opacity: 0;
  transform: scale(0.95) translateY(-20px);
}

/* ==========================================================================
   RESPONSIVE DESIGN
   ========================================================================== */

@media (max-width: 768px) {
  .app-modal {
    padding: var(--space-2, 0.5rem);
    padding-top: calc(var(--top-bar-height-mobile, 56px) + var(--quick-actions-height-mobile, 48px) + var(--space-2, 0.5rem));
  }
  
  .app-modal__container {
    max-width: calc(100vw - var(--space-4, 1rem));
    max-height: calc(100vh - var(--top-bar-height-mobile, 56px) - var(--quick-actions-height-mobile, 48px) - var(--space-4, 1rem));
  }
    .app-modal--small .app-modal__container,
  .app-modal--medium .app-modal__container,
  .app-modal--large .app-modal__container,
  .app-modal--xlarge .app-modal__container {
    max-width: calc(100vw - var(--space-4, 1rem));
  }
  
  .app-modal__header,
  .app-modal__body,
  .app-modal__footer {
    padding: var(--space-3, 0.75rem);
  }
  
  .app-modal__header-content {
    margin-right: var(--space-3, 0.75rem);
  }
  
  .app-modal__actions {
    flex-direction: column-reverse;
    gap: var(--space-2, 0.5rem);
  }
  
  .app-modal__btn {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .app-modal {
    padding: var(--space-1, 0.25rem);
    padding-top: calc(var(--top-bar-height-mobile, 56px) + var(--quick-actions-height-mobile, 48px) + var(--space-1, 0.25rem));
    align-items: flex-start; /* Changed from flex-end to flex-start */
  }
    .app-modal__container {
    max-width: 100vw;
    max-height: calc(95vh - var(--top-bar-height-mobile, 56px) - var(--quick-actions-height-mobile, 48px));
    border-radius: var(--radius-lg, 12px);
  }
  
  .app-modal__header,
  .app-modal__body,
  .app-modal__footer {
    padding: var(--space-3, 0.75rem);
  }
}

/* ==========================================================================
   ACCESSIBILITY
   ========================================================================== */

.app-modal:focus {
  outline: none;
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .modal-enter-active, .modal-leave-active,
  .modal-enter-active .app-modal__container,
  .modal-leave-active .app-modal__container,
  .app-modal__close {
    transition: none;
  }
}

/* High contrast mode */
@media (prefers-contrast: high) {
  .app-modal__container {
    border: 2px solid currentColor;
  }
  
  .app-modal__close {
    border: 2px solid currentColor;
  }
}

/* Global body scroll lock */
:global(.modal-open) {
  overflow: hidden;
}
</style>
